import {useMemo} from 'react';
import {
  fetchOverview,
  fetchResolvedCasesOverview,
} from '../../../../requests/queries/flows';
import {useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useGatedQuery} from './useGatedQuery';

export const useFlowOverview = ({filters, enabled = false}) => {
  const {slug} = useParams();
  const {
    auth: {userDetails},
  } = useSelector(state => state);

  const params = useMemo(
    () => ({
      project_id: slug || '',
      company_id: userDetails?.company?.id,
      ...filters,
    }),
    [filters, slug, userDetails?.company?.id],
  );

  const {data, isLoading} = useGatedQuery(
    ['overview-d', {params}],
    () => fetchOverview({params}),
    {enabled},
  );

  const {data: resolvedCasesOverview, isLoading: resolvedCasesLoading} =
    useGatedQuery(
      ['resolved-overview', {params}],
      () => fetchResolvedCasesOverview({params}),
      {enabled},
    );

  const overviewData = useMemo(
    () => (!isLoading && data && data?.entity) || [],
    [data, isLoading],
  );

  const resolvedCasesData = useMemo(
    () =>
      (!resolvedCasesLoading &&
        resolvedCasesOverview &&
        resolvedCasesOverview?.entity) ||
      [],
    [resolvedCasesOverview, resolvedCasesLoading],
  );

  const calculateTotals = data => {
    if (!Array.isArray(data)) return;
    return data?.reduce(
      (acc, item) => {
        acc.sum_amount += item.sum_amount || 0;
        acc.count += +item.count || 0;
        return acc;
      },
      {sum_amount: 0, count: 0},
    );
  };

  const TOTAL_EVENTS = useMemo(
    () => calculateTotals(overviewData),
    [overviewData],
  );
  const TOTAL_ALLOWED = useMemo(
    () =>
      calculateTotals(
        overviewData?.filter(data => data?.final_outcome === 'ALLOW'),
      ),
    [overviewData],
  );
  const TOTAL_PENDING = useMemo(
    () =>
      calculateTotals(
        overviewData?.filter(data => data?.final_outcome === 'PENDING'),
      ),
    [overviewData],
  );
  const TOTAL_BLOCKED = useMemo(
    () =>
      calculateTotals(
        overviewData?.filter(data => data?.final_outcome === 'BLOCK'),
      ),
    [overviewData],
  );

  const totalCases = useMemo(() => {
    if (!resolvedCasesData) return 0;
    return resolvedCasesData.reduce(
      (sum, item) => sum + parseInt(item.count || 0),
      0,
    );
  }, [resolvedCasesData]);

  const resolvedCases = useMemo(() => {
    if (!resolvedCasesData) return 0;
    const filtered = resolvedCasesData?.filter(item =>
      ['ALLOW', 'BLOCK']?.includes(item?.final_outcome),
    );
    return filtered.reduce((sum, item) => sum + parseInt(item.count || 0), 0);
  }, [resolvedCasesData]);

  const unresolvedCases = useMemo(() => {
    if (!overviewData) return 0;
    const filtered = resolvedCasesData?.filter(item =>
      ['PENDING']?.includes(item?.final_outcome),
    );
    return filtered.reduce((sum, item) => sum + parseInt(item.count || 0), 0);
  }, [overviewData, resolvedCasesData]);

  const RESOLVED_ALLOWED = useMemo(
    () =>
      calculateTotals(
        resolvedCasesData?.filter(data => data?.final_outcome === 'ALLOW'),
      ),
    [resolvedCasesData],
  );

  const RESOLVED_BLOCKED = useMemo(
    () =>
      calculateTotals(
        resolvedCasesData?.filter(data => data?.final_outcome === 'BLOCK'),
      ),
    [resolvedCasesData],
  );

  return {
    TOTAL_EVENTS,
    TOTAL_BLOCKED,
    TOTAL_ALLOWED,
    TOTAL_PENDING,
    totalCases,
    resolvedCases,
    unresolvedCases,
    RESOLVED_ALLOWED,
    RESOLVED_BLOCKED,
    totalResolved: resolvedCases,
    isLoading,
    overviewData: data,
  };
};
